<template>
  <base-modal :visible="visible" :width="'480px'" @close="close">
    <template #title>
      <i class="fas" :class="mode === 'add' ? 'fa-plus-circle' : 'fa-list-ul'"></i>
      <span>{{ title }}</span>
    </template>

    <div class="soa">
      <!-- Переключатель режима (только если разрешено добавление) -->
      <div v-if="allowAdd" class="soa__tabs">
        <button
          type="button"
          class="soa__tab"
          :class="{ 'soa__tab--active': mode === 'browse' }"
          @click="mode = 'browse'"
        >
          <i class="fas fa-list-ul"></i> Выбрать из списка
        </button>
        <button
          type="button"
          class="soa__tab"
          :class="{ 'soa__tab--active': mode === 'add' }"
          @click="mode = 'add'"
        >
          <i class="fas fa-plus-circle"></i> Добавить новое
        </button>
      </div>
      <!-- Подсказка когда только список -->
      <div v-else class="soa__only">Выберите из готовых вариантов</div>

      <!-- Режим: выбор из списка -->
      <div v-show="mode === 'browse'" class="soa__browse">
        <c-f-input
          ref="search"
          v-model="query"
          icon="fas fa-search"
          placeholder="Поиск..."
        />

        <div class="soa__list">
          <template v-if="filtered.length">
            <button
              v-for="(opt, idx) in filtered"
              :key="optId(opt) || `o-${idx}`"
              type="button"
              class="soa__item"
              :class="{ 'soa__item--selected': optLabel(opt) === optLabel(value) }"
              @click="choose(opt)"
            >
              {{ optLabel(opt) }}
            </button>
          </template>
          <div v-else class="soa__empty u-text-tertiary">Ничего не найдено</div>
        </div>
      </div>

      <!-- Режим: добавление нового (только если allowAdd) -->
      <div v-if="allowAdd && mode === 'add'" class="soa__add">
        <c-f-input
          ref="newField"
          v-model="newValue"
          :placeholder="'Введите новое значение'"
        />
        <div class="soa__add-actions">
          <app-button size="sm" icon="fas fa-check" :disabled="!newValue.trim()" @click="addNew">
            Добавить
          </app-button>
          <app-button size="sm" variant="ghost" icon="fas fa-times" @click="mode = 'browse'">Отмена</app-button>
        </div>
      </div>
    </div>
  </base-modal>
</template>

<script>
export default {
  name: 'SelectOrAdd',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: 'Выбор' },
    value: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    // Если false — только выбор из списка (без «Добавить новое»)
    allowAdd: { type: Boolean, default: true }
  },
  data() {
    return {
      mode: 'browse', // 'browse' | 'add'
      query: '',
      newValue: ''
    }
  },
  computed: {
    filtered() {
      const q = this.query.trim().toLowerCase()
      const all = this.options || []
      if (!q) return all
      return all.filter(o => this.optLabel(o).toLowerCase().includes(q))
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.mode = 'browse'
        this.query = ''
        this.newValue = ''
        this.focus()
      }
    }
  },
  methods: {
    // Элемент может быть строкой или объектом { id, name }
    optLabel(opt) {
      if (!opt) return ''
      return typeof opt === 'object' ? (opt.name ? opt.name : '') : opt
    },
    optId(opt) {
      if (!opt) return null
      return typeof opt === 'object' ? (opt.id !== undefined ? opt.id : null) : null
    },
    // Приводим к единому объекту { id, name } для события
    toOption(item) {
      const label = this.optLabel(item)
      const id = this.optId(item)
      const custom = id === null || id === undefined
      return { id: id !== null && id !== undefined ? id : null, name: label, custom }
    },
    focus() {
      this.$nextTick(() => {
        setTimeout(() => {
          const field = this.mode === 'add' ? this.$refs.newField : this.$refs.search
          if (field && field.$el) {
            const input = field.$el.querySelector('input')
            if (input) input.focus()
          }
        }, 120)
      })
    },
    choose(opt) {
      if (this.options.indexOf(opt) === -1) this.$emit('add-option', this.toOption(opt))
      this.$emit('select', this.toOption(opt))
      this.close()
    },
    addNew() {
      const v = this.newValue.trim()
      if (!v) return
      const opt = { id: null, name: v, custom: true }
      this.$emit('add-option', opt)
      this.$emit('select', opt)
      this.close()
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.soa {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.soa__tabs {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.soa__only {
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
}

.soa__tab {
  flex: 1;
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  background: var(--bg-surface-muted);
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  padding: var(--space-4) var(--space-6);
  font-size: var(--text-sm);
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  transition: background var(--duration-fast) var(--ease-base),
              border-color var(--duration-fast) var(--ease-base),
              color var(--duration-fast) var(--ease-base);
}
.soa__tab:hover { background: var(--bg-subtle); }
.soa__tab--active {
  background: var(--bg-selected);
  border-color: var(--border-selected);
  color: var(--color-brand-700);
  font-weight: var(--font-weight-medium);
}
.soa__tab i { font-size: var(--text-xs); }

.soa__list {
  margin-top: var(--space-8);
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-0-5);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  border-radius: var(--radius-md);
  padding: var(--space-2);
}

.soa__item {
  text-align: left;
  padding: var(--space-5) var(--space-8);
  cursor: pointer;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  font-size: var(--text-base);
  color: var(--text-primary);
  transition: background var(--duration-fast) var(--ease-base);
}
.soa__item:hover { background: var(--bg-selected); }
.soa__item--selected { background: var(--bg-selected); font-weight: var(--font-weight-medium); }

.soa__empty {
  padding: var(--space-5) var(--space-8);
  font-style: italic;
  font-size: var(--text-base);
}

.soa__add {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  padding-top: var(--space-4);
}

.soa__add-actions {
  display: flex;
  gap: var(--space-6);
}
.soa__add-actions .abtn { flex: 1; justify-content: center; }
</style>
